/* eslint-disable no-unused-vars */
/**
 * This class is used to dynamically load css and javascript files into a document.
 * This loading of resources is done in sequence
 */
class BocResourceManager {
/* eslint-enable no-unused-vars */

    static inProgress = Promise.resolve();
    /* eslint-disable no-undef */
    static baseUrl = bocVars.homeUrl.replace(/\/$/, '');
    /* eslint-enable no-undef */

    /**
     * Checks if the stylesheet being added already exists. Make sure the homeUrl is
     * properly set
     *
     * @param {string} path The relative path to the resource.
     * @returns {boolean} 'true' if the stylesheet already exists in the document, false otherwise
     */
    static stylesheetExist(path) {
        let scripts = document.querySelectorAll('link[type="text/css"]');
        for (let index = 0 ; index < scripts.length ; index++) {
            let url = scripts[index].getAttribute('href');
            if (url && url.includes(path)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Checks if the specified script already exists in the document. Make sure the homeUrl is
     * properly set.
     *
     * @param {string} path The relative path to the resource.
     * @returns {boolean} 'true' if the script already exists in the document, false otherwise
     */
    static scriptExist(path) {
        let scripts = document.querySelectorAll('script');
        for (let index = 0 ; index < scripts.length ; index++) {
            let url = scripts[index].getAttribute('src');
            if (url && url.includes(path)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Loads the specified resource
     * @param {string} resource The relative path to the resource
     * @returns {Promise|boolean} A promise that will resolve when the resource has loaded
     */
    static loadResource(resource) {
        let extension = BocResourceManager.getFileExtension(resource);
        switch (extension) {
            case 'css':
                return BocResourceManager.loadStylesheet(resource);
            case 'js':
                return BocResourceManager.loadScript(resource);
            default:
                return false;
        }
    }

    /**
     * Loads the specified script
     * @param {string} resource The relative path to the resource
     * @returns {Promise} A promise that will resolve when the script has loaded
     */
    static loadScript(resource) {
        return new Promise((resolve, reject) => {
            if (BocResourceManager.scriptExist(resource)) {
                resolve();
                return;
            }
            let url = BocResourceManager.baseUrl + resource;
            let script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = url;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
        });
    }

    /**
     * Loads the specified stylesheet
     * @param {path} resource The relative path to the resource
     * @returns {Promise} A promise that resolves when the stylesheet has been loaded
     */
    static loadStylesheet(resource) {
        return new Promise((resolve, reject) => {
            if (BocResourceManager.stylesheetExist(resource)) {
                resolve();
                return;
            }
            let url = BocResourceManager.baseUrl + resource;
            let style = document.createElement('link');
            style.type = 'text/css';
            style.rel = 'stylesheet';
            style.href = url;
            style.onload = resolve;
            style.onerror = reject;
            document.head.appendChild(style);
        });
    }

    /**
     * Adds the resource to the queue of resources that need to be loaded
     * @param {string} resource The relative path to the resource that will be added to the queue
     * @returns {Promise} A promise that will resolve when the resource has been loaded
     */
    static queueLoadingResource(resource) {
        const newResource = () => {
            return BocResourceManager.loadResource(resource);
        };

        BocResourceManager.inProgress = BocResourceManager.inProgress.then(newResource);
        return BocResourceManager.inProgress;
    }

    /**
     * Gets the extension of the file. e.g. "css", "js"
     * @param {string} filePath The relative path to the resource
     * @returns {string} The extension of the file
     */
    static getFileExtension(filePath) {
        return (filePath.split('.').pop()).toLowerCase();
    }

    /**
     * Sends requests repeatedly until the desired resource is found and the condition applied to the data is true.
     *
     *  How the condition callback works:
     *     The condition is a callback that takes the fetched data as input parameter.
     *        function findSeries(data) {
     *            // If data has what you are looking for, return true
     *            // otherwise return false.
     *        }
     *
     * @param {string} url The resource location
     * @param {int} interval The time delay in milliseconds between polls
     * @param {function} condition Condition callback to determine when to stop polling.
     * @param {string} format In what format do you want the returned data ('json' or 'text') default 'json'
     * @returns {Promise<any|string>} Returns a promise that will resolve once the desired resource if found
     */
    static async poll(url, interval, condition, format = 'json') {
        const options = {};
        const response = await fetch(url, options);

        if (!response.ok) {
            await new Promise(resolve => setTimeout(resolve, interval));
            return BocResourceManager.poll(url, interval, condition);
        }

        const data =  format === 'json' ? await response.json() : await response.text();

        if (typeof condition === 'function') {
            if (!condition(data)) {
                // Try again after certain interval of time if the condition was not met
                await new Promise(resolve => setTimeout(resolve, interval));

                // Poll again (this is a recursive call)
                return BocResourceManager.poll(url, interval, condition);
            }
        }

        // Resolve the promise with the fetched data
        return data;
    }
}
